import { isReportingPendingResponse, usageTimelineLedgerEstimatedBytes, USAGE_TIMELINE_HTTP_CACHE_MAX_BYTES } from '../utils/usageTimelineTransport';
import { fetchWithChatFeedback } from './chatFeedbackRequired';
import { parseUsageHistoryBounds, UsageHistoryBounds } from '../pages/usageDashboardDatePolicy';
/**
 * Admin Dashboard API Functions
 * Handles admin status checks, usage summary, user detail, and email-sent tracking
 */

import {
  UsageSummary,
  UsageTimelineLedger,
  UsageDashboardStatsReport,
  UsageDashboardOverviewReport,
  FlowDetail,
  AccessRequestRecord,
  NewestUserRecord,
  ChatFeedbackSummary,
  OrganizationalAdoptionReport,
} from '../types/admin';
import { extractDetailMessage, normalizeApiError } from '../utils/userFacingError';
import { normalizeUsageTimelineDurationEvidence } from '../utils/usageTimelineDurationEvidence';
export { USAGE_TIMELINE_HTTP_CACHE_MAX_BYTES } from '../utils/usageTimelineTransport';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

function getAuthHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
  };
}

/**
 * Fetch admin capabilities in a single round-trip.
 * `GET /api/admin/is-admin` returns the dashboard-super and User Feedback
 * staff capabilities separately. Older backends fail closed for either one.
 */
export async function checkAdminStatus(): Promise<{
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isActorSuperAdmin?: boolean;
  isUserFeedbackStaff: boolean;
}> {
  try {
    const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/admin/is-admin`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      return { isAdmin: false, isSuperAdmin: false, isActorSuperAdmin: false, isUserFeedbackStaff: false };
    }
    const data = await response.json();
    return {
      isAdmin: data.is_admin === true,
      isSuperAdmin: data.is_super_admin === true,
      // The actor identity is used only to keep the role switcher available.
      isActorSuperAdmin: data.is_actor_super_admin === true,
      isUserFeedbackStaff: data.is_user_feedback_staff === true,
    };
  } catch {
    return { isAdmin: false, isSuperAdmin: false, isActorSuperAdmin: false, isUserFeedbackStaff: false };
  }
}

/**
 * Check if the current user is an admin
 */
export async function checkIsAdmin(): Promise<boolean> {
  const { isAdmin } = await checkAdminStatus();
  return isAdmin;
}

/**
 * Check if the current user is a super admin
 */
export async function checkIsSuperAdmin(): Promise<boolean> {
  const { isSuperAdmin } = await checkAdminStatus();
  return isSuperAdmin;
}

/**
 * Coalesce concurrent identical detailed usage-summary requests. The Usage
 * Dashboard's date-range report and export-adjacent consumers may request the
 * same window and actor scope together; keying the in-flight Promise here
 * collapses those into a single backend call.
 * The entry is removed as soon as the request settles (resolve OR reject) so a
 * later request for the same window issues a fresh fetch.
 */
interface UsageSummaryInFlightOwner {
  promise: Promise<UsageSummary>;
  controller: AbortController;
  consumers: number;
}
const usageSummaryInFlight = new Map<string, UsageSummaryInFlightOwner>();
const usageDashboardStatsInFlight = new Map<string, Promise<UsageDashboardStatsReport>>();
const usageDashboardOverviewInFlight = new Map<string, Promise<UsageDashboardOverviewReport>>();
const usageDashboardOverviewControllers = new Map<string, AbortController>();
const usageDashboardOverviewConsumerCounts = new Map<string, number>();
const organizationalAdoptionInFlight = new Map<string, Promise<OrganizationalAdoptionReport>>();
const usageTimelineInFlight = new Map<string, Promise<UsageTimelineLedger>>();
const usageTimelineControllers = new Map<string, AbortController>();
const usageTimelineReadinessInFlight = new Map<string, Promise<UsageTimelineReadiness>>();
export type UsageTimelineReadiness = 'hot' | 'cold' | 'unknown';
export type UsageTimelineClientTimingStage = 'response-received' | 'json-decoded' | 'not-modified-body-reused';

interface UsageTimelineHttpCacheEntry {
  etag: string;
  ledger: UsageTimelineLedger;
  byteSize: number;
  expiresAt: number;
}
export const USAGE_TIMELINE_HTTP_CACHE_MAX_ENTRIES = 4;
export const USAGE_TIMELINE_HTTP_CACHE_TTL_MS = 15 * 60 * 1000;
// A module-scoped owner must eventually settle. Otherwise a lost browser
// connection leaves every later same-scope request permanently joined to a
// promise that can never produce an authorization-validated result.
export const USAGE_TIMELINE_REQUEST_TIMEOUT_MS = 30 * 1000;
const usageTimelineHttpCache = new Map<string, UsageTimelineHttpCacheEntry>();
let usageTimelineHttpCacheBytes = 0;
let unscopedUsageTimelineRequestId = 0;
let usageDashboardClientGeneration = 0;
const usageTimelineOwnerGeneration = new Map<string, number>();

function protectedRequestScope(scope: string | undefined): string {
  return typeof scope === 'string' && scope.length > 0
    ? scope
    : `unscoped:${++unscopedUsageTimelineRequestId}`;
}

/** Clear protected same-tab state on logout, account, or capability changes. */
export function clearUsageDashboardProtectedClientState(): void {
  usageDashboardClientGeneration += 1;
  Array.from(usageSummaryInFlight.values()).forEach(({ controller }) => controller.abort());
  usageSummaryInFlight.clear();
  usageDashboardStatsInFlight.clear();
  usageDashboardOverviewInFlight.clear();
  Array.from(usageDashboardOverviewControllers.values()).forEach((controller) => controller.abort());
  usageDashboardOverviewControllers.clear();
  usageDashboardOverviewConsumerCounts.clear();
  organizationalAdoptionInFlight.clear();
  Array.from(usageTimelineControllers.values()).forEach((controller) => controller.abort());
  usageTimelineControllers.clear();
  usageTimelineInFlight.clear();
  usageTimelineReadinessInFlight.clear();
  usageTimelineOwnerGeneration.clear();
  usageTimelineHttpCache.clear();
  usageTimelineHttpCacheBytes = 0;
}

/**
 * Keep actor scopes canonical for both URL serialization and in-flight request
 * coalescing. Unit names themselves remain unchanged because the backend owns
 * their canonical interpretation; only duplicate selections and input order
 * are normalized here.
 */
function normalizeUsageSummaryUnits(units?: readonly string[]): string[] {
  return Array.from(new Set(units ?? [])).sort();
}

/**
 * The server derives authorization from the session cookie, so the local scope
 * key is intentionally not serialized. It only prevents an in-flight request
 * opened for one mounted dashboard identity/capability scope from being shared
 * with another before React clears its L1 cache.
 */
function usageDashboardInFlightKey(
  start: string,
  end: string,
  units: readonly string[] | undefined,
  inFlightScopeKey: string | undefined
): string {
  return JSON.stringify([start, end, normalizeUsageSummaryUnits(units), protectedRequestScope(inFlightScopeKey)]);
}

/**
 * Fetch usage summary for a date range.
 *
 * Concurrent calls for the same window, normalized actor scope, and optional
 * authenticated presentation scope share one network request (dedup). The
 * scope is intentionally local-only (it is not sent to the server): it keeps
 * an account/capability transition from joining a request that began under a
 * different authenticated cookie. Each caller's signal detaches that caller;
 * the shared owner's transport is aborted only when the last caller leaves.
 * Settled responses are not retained, and direct requests have no Timeline deadline.
 */
export async function fetchUsageSummary(
  start: string,
  end: string,
  units?: readonly string[],
  inFlightScopeKey?: string,
  options?: { signal?: AbortSignal },
): Promise<UsageSummary> {
  const normalizedUnits = normalizeUsageSummaryUnits(units);
  const key = JSON.stringify([start, end, normalizedUnits, protectedRequestScope(inFlightScopeKey)]);
  const existing = usageSummaryInFlight.get(key);
  if (existing) return awaitUsageSummaryConsumer(key, existing, options?.signal);

  const unitParams = normalizedUnits
    .map((unit) => 'units=' + encodeURIComponent(unit))
    .join('&');
  const query = 'start=' + encodeURIComponent(start) + '&end=' + encodeURIComponent(end)
    + (unitParams ? '&' + unitParams : '');
  const clientGeneration = usageDashboardClientGeneration;
  const controller = new AbortController();

  const request = (async (): Promise<UsageSummary> => {
    const response = await fetchWithChatFeedback(
      API_BASE_URL + '/api/admin/usage-summary?' + query,
      {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
        signal: controller.signal,
      },
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to fetch usage summary' }));
      const normalized = normalizeApiError(error);
      const err = new Error(normalized.message) as Error & {
        status?: number; error_code?: string; retryable?: boolean; retry_after_ms?: number;
      };
      err.status = response.status;
      if (normalized.errorCode) err.error_code = normalized.errorCode;
      const retryAfter = response.headers?.get('Retry-After');
      const retrySeconds = retryAfter == null ? Number.NaN : Number(retryAfter);
      if (Number.isFinite(retrySeconds) && retrySeconds >= 0) {
        err.retry_after_ms = Math.min(Math.round(retrySeconds * 1_000), 5_000);
      }
      if (isReportingPendingResponse(response.status, normalized.errorCode)) err.retryable = true;
      throw err;
    }
    const data = await response.json() as UsageSummary;
    if (clientGeneration !== usageDashboardClientGeneration) throw createAbortError();
    return data;
  })();

  const owner: UsageSummaryInFlightOwner = { promise: request, controller, consumers: 0 };
  usageSummaryInFlight.set(key, owner);
  void request.then(
    () => { if (usageSummaryInFlight.get(key) === owner) usageSummaryInFlight.delete(key); },
    () => { if (usageSummaryInFlight.get(key) === owner) usageSummaryInFlight.delete(key); },
  );
  return awaitUsageSummaryConsumer(key, owner, options?.signal);
}

/**
 * Detach one direct-summary caller from a shared owner. Navigation or a newer
 * range cancels the actual fetch only when this was the last consumer, while a
 * remount that joined the same request can continue to receive its result.
 */
function awaitUsageSummaryConsumer(
  key: string,
  owner: UsageSummaryInFlightOwner,
  signal?: AbortSignal,
): Promise<UsageSummary> {
  let released = false;
  const release = (abortWhenLastConsumerDetaches: boolean) => {
    if (released) return;
    released = true;
    owner.consumers = Math.max(0, owner.consumers - 1);
    if (
      abortWhenLastConsumerDetaches
      && owner.consumers === 0
      && usageSummaryInFlight.get(key) === owner
    ) {
      usageSummaryInFlight.delete(key);
      owner.controller.abort();
    }
  };

  if (signal?.aborted) {
    // An already-aborted caller must never cancel a request a sibling already
    // joined. A just-created owner has no consumers yet, so it is safe to stop
    // that otherwise-orphaned transport.
    if (owner.consumers === 0 && usageSummaryInFlight.get(key) === owner) {
      usageSummaryInFlight.delete(key);
      owner.controller.abort();
    }
    return Promise.reject(createAbortError());
  }

  owner.consumers += 1;
  if (!signal) return owner.promise.finally(() => release(false));

  return new Promise((resolve, reject) => {
    const onAbort = () => {
      signal.removeEventListener('abort', onAbort);
      release(true);
      reject(createAbortError());
    };
    signal.addEventListener('abort', onAbort, { once: true });
    owner.promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        release(false);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', onAbort);
        release(false);
        reject(error);
      },
    );
  });
}

/**
 * Fetch a requested detailed range, optionally asking the server for one
 * bounded (maximum 31-day) display envelope around it. Exact authorized calls
 * share one module-scoped owner; each caller's AbortSignal only detaches that
 * consumer so route navigation cannot cancel work a remount has joined.
 */
export async function fetchUsageTimeline(
  start: string,
  end: string,
  units?: readonly string[],
  options?: {
    coverageStart?: string;
    coverageEnd?: string;
    signal?: AbortSignal;
    cacheScopeKey?: string;
    forceRevalidate?: boolean;
    invalidateInFlight?: boolean;
    onTiming?: (stage: UsageTimelineClientTimingStage) => void;
  },
): Promise<UsageTimelineLedger> {
  const normalizedUnits = normalizeUsageSummaryUnits(units);
  const parts = [
    `start=${encodeURIComponent(start)}`,
    `end=${encodeURIComponent(end)}`,
    ...normalizedUnits.map((unit) => `units=${encodeURIComponent(unit)}`),
  ];
  if (options?.coverageStart) parts.push(`coverage_start=${encodeURIComponent(options.coverageStart)}`);
  if (options?.coverageEnd) parts.push(`coverage_end=${encodeURIComponent(options.coverageEnd)}`);
  // Older servers ignore this optional query and return the supported inline
  // shape. Existing tabs without it keep actor/activity proof inline while
  // new tabs negotiate the compact representation explicitly.
  parts.push('duration_evidence=canonical-v1');
  const url = `${API_BASE_URL}/api/admin/usage-timeline?${parts.join('&')}`;
  // Reuse is allowed only when the caller supplies the complete local
  // identity/capability/unit scope. An omitted scope gets a one-shot key so a
  // future non-Dashboard caller cannot cross an account transition.
  const reusableScope = typeof options?.cacheScopeKey === 'string' && options.cacheScopeKey.length > 0;
  const callerGeneration = usageDashboardClientGeneration;
  const cacheKey = JSON.stringify([url, protectedRequestScope(options?.cacheScopeKey)]);
  const existingOwner = usageTimelineInFlight.get(cacheKey);
  // A force revalidation means "do not accept an already settled body". It
  // must still join the exact in-flight validation owner: starting another
  // identical authorized request adds load and permits completion races.
  if (existingOwner && options?.invalidateInFlight) {
    usageTimelineOwnerGeneration.set(cacheKey, (usageTimelineOwnerGeneration.get(cacheKey) ?? 0) + 1);
    usageTimelineControllers.get(cacheKey)?.abort();
    // Wait for the retired owner before issuing the mutation-era request. A
    // fetch may resolve after abort, but its owner token has already fenced it.
    const replaceAfterRetirement = () => {
      // A mutation-triggered follower is tied to the caller's protected
      // generation. Logout/account transition or consumer detach while it
      // waits must not launch a new request under an old scope.
      if (callerGeneration !== usageDashboardClientGeneration || options.signal?.aborted) {
        throw createAbortError();
      }
      return fetchUsageTimeline(start, end, units, { ...options, invalidateInFlight: false });
    };
    return awaitTimelineConsumer(
      existingOwner.then(replaceAfterRetirement, replaceAfterRetirement), options?.signal,
    );
  }
  if (existingOwner) {
    return awaitTimelineConsumer(existingOwner, options?.signal);
  }

  const cachedCandidate = reusableScope ? usageTimelineHttpCache.get(cacheKey) : undefined;
  const cached = cachedCandidate && cachedCandidate.expiresAt > Date.now() ? cachedCandidate : undefined;
  if (cachedCandidate && !cached) deleteUsageTimelineHttpCache(cacheKey);
  const headers = getAuthHeaders();
  if (cached) headers['If-None-Match'] = cached.etag;
  const clientGeneration = usageDashboardClientGeneration;
  const ownerGeneration = (usageTimelineOwnerGeneration.get(cacheKey) ?? 0) + 1;
  usageTimelineOwnerGeneration.set(cacheKey, ownerGeneration);
  const ownerController = new AbortController();
  let ownerTimedOut = false;
  const ownerTimeout = window.setTimeout(
    () => { ownerTimedOut = true; ownerController.abort(); },
    USAGE_TIMELINE_REQUEST_TIMEOUT_MS,
  );
  const owner = (async (): Promise<UsageTimelineLedger> => {
    try {
      const response = await fetchWithChatFeedback(url, {
        method: 'GET',
        headers,
        credentials: 'include',
        signal: ownerController.signal,
        // The owner is module-scoped. A component unmount detaches its consumer
        // below but cannot abort work a remount or sibling has already joined.
      });
      options?.onTiming?.('response-received');
      // A server-side owner can explicitly report that the canonical timeline
      // build is still running. `Response.ok` includes 202, so recognize the
      // one supported pending DTO before any ledger decoding/cache writes.
      if (response.status === 202) {
        const body = await response.json().catch(() => ({}));
        const normalized = normalizeApiError(body);
        const pendingCode = body !== null
          && typeof body === 'object'
          && (body as { detail?: unknown }).detail !== null
          && typeof (body as { detail?: unknown }).detail === 'object'
          && (body as { detail: { code?: unknown } }).detail.code === 'timeline_build_pending';
        const err = new Error(normalized.message) as Error & {
          status?: number;
          error_code?: string;
          retry_after_ms?: number;
          retryable?: boolean;
        };
        err.status = 202;
        if (pendingCode) err.error_code = 'timeline_build_pending';
        const retryAfter = response.headers?.get('Retry-After');
        const retryAfterSeconds = retryAfter == null ? Number.NaN : Number(retryAfter);
        if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds >= 0) {
          err.retry_after_ms = Math.min(Math.round(retryAfterSeconds * 1_000), 5_000);
        }
        // Fail closed for an unexpected successful-looking DTO. Only the
        // documented pending response participates in bounded polling.
        if (pendingCode) err.retryable = true;
        throw err;
      }
      if (response.status === 304) {
        if (!cached) throw new Error('The usage timeline cache could not satisfy the server validation response.');
        const freshUntil = response.headers?.get('X-Usage-Timeline-Fresh-Until');
        // Keep the validated decoded ledger object and every derived WeakMap
        // cache intact. A 304 has no DTO body, so cloning/reindexing/stringifying
        // it would make a cheap conditional request expensive in the browser.
        if (freshUntil && cached.ledger.coverage) {
          cached.ledger.coverage.current_day_fresh_until = freshUntil;
        }
        const renewed = { ...cached, expiresAt: Date.now() + USAGE_TIMELINE_HTTP_CACHE_TTL_MS };
        if (!isCurrentTimelineOwner(cacheKey, clientGeneration, ownerGeneration)) throw createAbortError();
        if (isCurrentTimelineOwner(cacheKey, clientGeneration, ownerGeneration)) {
          promoteUsageTimelineHttpCache(cacheKey, renewed);
        }
        options?.onTiming?.('not-modified-body-reused');
        return cached.ledger;
      }
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to fetch usage timeline' }));
        const normalized = normalizeApiError(error);
        const err = new Error(normalized.message) as Error & {
          status?: number;
          error_code?: string;
          retry_after_ms?: number;
          retryable?: boolean;
        };
        err.status = response.status;
        if (normalized.errorCode) err.error_code = normalized.errorCode;
        // `Retry-After` is a transport header, not a user-facing message. Keep a
        // bounded numeric form on this typed client error so the dashboard can
        // join an existing server-side build without inventing another job.
        const retryAfter = response.headers?.get('Retry-After');
        const retryAfterSeconds = retryAfter == null ? Number.NaN : Number(retryAfter);
        if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds >= 0) {
          err.retry_after_ms = Math.min(Math.round(retryAfterSeconds * 1_000), 5_000);
        }
        // Publication races and a busy shared calculation can also briefly
        // delay a consistent response. Let the caller use its normal bounded
        // retry protocol without starting another fetch here.
        if (isReportingPendingResponse(response.status, normalized.errorCode)) {
          err.retryable = true;
        }
        throw err;
      }
      const ledger = await response.json() as UsageTimelineLedger;
      if (ledger.duration_evidence !== undefined && !normalizeUsageTimelineDurationEvidence(ledger)) {
        throw new Error('The usage timeline response contained invalid workflow evidence.');
      }
      if (!isCurrentTimelineOwner(cacheKey, clientGeneration, ownerGeneration)) throw createAbortError();
      options?.onTiming?.('json-decoded');
      const etag = response.headers?.get('ETag');
      if (etag && reusableScope && isCurrentTimelineOwner(cacheKey, clientGeneration, ownerGeneration)) {
        writeUsageTimelineHttpCache(cacheKey, {
          etag,
          ledger,
          byteSize: usageTimelineLedgerEstimatedBytes(ledger),
          expiresAt: Date.now() + USAGE_TIMELINE_HTTP_CACHE_TTL_MS,
        });
      }
      return ledger;
    } catch (error) {
      if (ownerTimedOut) throw createTimelineTimeoutError();
      throw error;
    } finally {
      window.clearTimeout(ownerTimeout);
    }
  })();
  usageTimelineInFlight.set(cacheKey, owner);
  usageTimelineControllers.set(cacheKey, ownerController);
  void owner.then(
    () => {
      if (usageTimelineInFlight.get(cacheKey) === owner) {
        usageTimelineInFlight.delete(cacheKey);
        usageTimelineControllers.delete(cacheKey);
      }
    },
    () => {
      if (usageTimelineInFlight.get(cacheKey) === owner) {
        usageTimelineInFlight.delete(cacheKey);
        usageTimelineControllers.delete(cacheKey);
      }
    },
  );
  return awaitTimelineConsumer(owner, options?.signal);
}

function isCurrentTimelineOwner(key: string, clientGeneration: number, ownerGeneration: number): boolean {
  return usageDashboardClientGeneration === clientGeneration
    && usageTimelineOwnerGeneration.get(key) === ownerGeneration;
}

function awaitTimelineConsumer(
  owner: Promise<UsageTimelineLedger>,
  signal?: AbortSignal,
): Promise<UsageTimelineLedger> {
  if (!signal) return owner;
  if (signal.aborted) return Promise.reject(createAbortError());
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      signal.removeEventListener('abort', onAbort);
      reject(createAbortError());
    };
    signal.addEventListener('abort', onAbort, { once: true });
    owner.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      },
    );
  });
}

function createAbortError(): Error {
  const error = new Error('Timeline request aborted.');
  error.name = 'AbortError';
  return error;
}

/**
 * The browser cannot tell whether aborting its transport deadline stopped the
 * server.  Treat that ambiguity differently from the explicit server-owned
 * pending-build DTO: a second automatic client request can overlap the first
 * expensive calculation, while a manual retry remains available to the user.
 */
function createTimelineTimeoutError(): Error & { retryable: false; error_code: 'timeline_request_timeout' } {
  const error = new Error(
    'The usage timeline request did not finish before the browser deadline. It may still be completing; please try again shortly.',
  ) as Error & {
    retryable: false; error_code: 'timeline_request_timeout';
  };
  error.name = 'TimelineRequestTimeoutError';
  error.retryable = false;
  error.error_code = 'timeline_request_timeout';
  return error;
}

function promoteUsageTimelineHttpCache(key: string, entry: UsageTimelineHttpCacheEntry): void {
  writeUsageTimelineHttpCache(key, entry);
}

function deleteUsageTimelineHttpCache(key: string): void {
  const entry = usageTimelineHttpCache.get(key);
  if (!entry) return;
  usageTimelineHttpCache.delete(key);
  usageTimelineHttpCacheBytes -= entry.byteSize;
}

function writeUsageTimelineHttpCache(key: string, entry: UsageTimelineHttpCacheEntry): void {
  const prior = usageTimelineHttpCache.get(key);
  if (prior) usageTimelineHttpCacheBytes -= prior.byteSize;
  usageTimelineHttpCache.delete(key);
  if (entry.byteSize > USAGE_TIMELINE_HTTP_CACHE_MAX_BYTES) return;
  usageTimelineHttpCache.set(key, entry);
  usageTimelineHttpCacheBytes += entry.byteSize;
  while (
    usageTimelineHttpCache.size > USAGE_TIMELINE_HTTP_CACHE_MAX_ENTRIES
    || usageTimelineHttpCacheBytes > USAGE_TIMELINE_HTTP_CACHE_MAX_BYTES
  ) {
    const oldestKey = usageTimelineHttpCache.keys().next().value as string | undefined;
    if (oldestKey === undefined) break;
    const oldest = usageTimelineHttpCache.get(oldestKey);
    usageTimelineHttpCache.delete(oldestKey);
    usageTimelineHttpCacheBytes -= oldest?.byteSize ?? 0;
  }
}

/**
 * Ask the authenticated server whether this exact detailed range/scope can be
 * served without source replay or a foreground build. The optimization is
 * deliberately fail-closed: only an explicit `hot` result changes scheduling;
 * old servers, errors, and unexpected payloads preserve overview-first loading.
 */
export async function fetchUsageTimelineReadiness(
  start: string,
  end: string,
  units?: readonly string[],
  inFlightScopeKey?: string,
): Promise<UsageTimelineReadiness> {
  const normalizedUnits = normalizeUsageSummaryUnits(units);
  const parts = [
    `start=${encodeURIComponent(start)}`,
    `end=${encodeURIComponent(end)}`,
    ...normalizedUnits.map((unit) => `units=${encodeURIComponent(unit)}`),
  ];
  const key = JSON.stringify([start, end, normalizedUnits, protectedRequestScope(inFlightScopeKey)]);
  const existing = usageTimelineReadinessInFlight.get(key);
  if (existing) return existing;
  const clientGeneration = usageDashboardClientGeneration;
  const request = (async (): Promise<UsageTimelineReadiness> => {
    try {
      const response = await fetchWithChatFeedback(
        `${API_BASE_URL}/api/admin/usage-timeline-readiness?${parts.join('&')}`,
        { method: 'GET', headers: getAuthHeaders(), credentials: 'include' },
      );
      if (!response.ok) return 'unknown';
      const payload = await response.json();
      if (clientGeneration !== usageDashboardClientGeneration) return 'unknown';
      return payload?.readiness === 'hot' || payload?.readiness === 'cold' || payload?.readiness === 'unknown'
        ? payload.readiness
        : 'unknown';
    } catch {
      return 'unknown';
    }
  })();
  usageTimelineReadinessInFlight.set(key, request);
  try {
    return await request;
  } finally {
    if (usageTimelineReadinessInFlight.get(key) === request) usageTimelineReadinessInFlight.delete(key);
  }
}
export async function fetchUsageDashboardAccess(
  units?: readonly string[],
  _inFlightScopeKey?: string,
): Promise<{ scope_revision: string; usage_serving_path: 'direct' | 'timeline'; history_bounds?: UsageHistoryBounds }> {
  const normalizedUnits = normalizeUsageSummaryUnits(units);
  const query = normalizedUnits.map((unit) => `units=${encodeURIComponent(unit)}`).join('&');
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/admin/usage-dashboard-access${query ? `?${query}` : ''}`,
    { method: 'GET', headers: getAuthHeaders(), credentials: 'include' },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to verify usage dashboard access' }));
    const err = new Error(extractDetailMessage(error.detail, `HTTP ${response.status}`)) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }
  const body = await response.json();
  const history_bounds = parseUsageHistoryBounds(body?.history_bounds);
  if ((body?.history_bounds !== undefined && !history_bounds) || typeof body?.scope_revision !== 'string' || body.scope_revision.length === 0) {
    throw new Error('Usage dashboard access verification returned an invalid scope.');
  }
  return { scope_revision: body.scope_revision, ...(history_bounds ? { history_bounds } : {}),
    usage_serving_path: body.usage_serving_path === 'timeline' ? 'timeline' : 'direct' };
}

/**
 * Fetch pre-aggregated Usage Dashboard KPI stats for the Today / Past Week /
 * Past Month control. This deliberately remains separate from usage-summary,
 * which is retained for the detailed grouped report and export.
 */
export async function fetchUsageDashboardStats(
  startDate: string,
  endDate: string,
  units?: readonly string[],
  // Kept in the client signature so dashboard callers can carry the same
  // scope input as their L1 cache. The server derives authorization from the
  // session; the local in-flight map may share work only within this exact
  // mounted identity/capability/scope key.
  _inFlightScopeKey?: string
): Promise<UsageDashboardStatsReport> {
  const normalizedUnits = normalizeUsageSummaryUnits(units);
  const key = usageDashboardInFlightKey(startDate, endDate, normalizedUnits, _inFlightScopeKey);
  const existing = usageDashboardStatsInFlight.get(key);
  if (existing) return existing;
  const unitParams = normalizedUnits
    .map(unit => `units=${encodeURIComponent(unit)}`)
    .join('&');
  const query = `start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`
    + (unitParams ? `&${unitParams}` : '');
  const clientGeneration = usageDashboardClientGeneration;
  const request = (async (): Promise<UsageDashboardStatsReport> => {
    const response = await fetchWithChatFeedback(
      `${API_BASE_URL}/api/admin/usage-dashboard-stats?${query}`,
      { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to fetch usage dashboard stats' }));
      const err = new Error(extractDetailMessage(error.detail, `HTTP ${response.status}`)) as any;
      err.status = response.status;
      throw err;
    }
    const data = await response.json();
    if (clientGeneration !== usageDashboardClientGeneration) throw createAbortError();
    return data;
  })();
  usageDashboardStatsInFlight.set(key, request);
  try {
    return await request;
  } finally {
    if (usageDashboardStatsInFlight.get(key) === request) usageDashboardStatsInFlight.delete(key);
  }
}

/**
 * Fetch the server-combined toggle overview. The backend constructs current
 * authorization/metadata context once and omits All Units when ineligible.
 * This client helper is used only by the eligible unfiltered super view; the
 * independent endpoints remain the compatibility path for every other scope.
 */
export async function fetchUsageDashboardOverview(
  startDate: string,
  endDate: string,
  units?: readonly string[],
  inFlightScopeKey?: string,
  options?: { signal?: AbortSignal; expireInFlightOnAbort?: boolean },
): Promise<UsageDashboardOverviewReport> {
  const normalizedUnits = normalizeUsageSummaryUnits(units);
  const key = usageDashboardInFlightKey(startDate, endDate, normalizedUnits, inFlightScopeKey);
  const existing = usageDashboardOverviewInFlight.get(key);
  if (existing) return awaitOverviewConsumer(key, existing, options?.signal, options?.expireInFlightOnAbort === true);
  const unitParams = normalizedUnits
    .map(unit => `units=${encodeURIComponent(unit)}`)
    .join('&');
  const query = `start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`
    + (unitParams ? `&${unitParams}` : '');
  const clientGeneration = usageDashboardClientGeneration;
  const ownerController = new AbortController();
  const request = (async (): Promise<UsageDashboardOverviewReport> => {
    const response = await fetchWithChatFeedback(
      `${API_BASE_URL}/api/admin/usage-dashboard-overview?${query}`,
      {
        method: 'GET', headers: getAuthHeaders(), credentials: 'include',
        signal: ownerController.signal,
      }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to fetch usage dashboard overview' }));
      const err = new Error(extractDetailMessage(error.detail, `HTTP ${response.status}`)) as any;
      err.status = response.status;
      throw err;
    }
    const data = await response.json() as UsageDashboardOverviewReport & { scope_revision?: unknown };
    // The body never authorizes a retained browser snapshot. Only the CORS-
    // exposed response header is produced from the server's response-time
    // policy recheck, so discard a forged/legacy body field before attaching
    // that authoritative value.
    delete data.scope_revision;
    const scopeRevision = response.headers?.get('X-Usage-Dashboard-Scope-Revision');
    if (typeof scopeRevision === 'string' && scopeRevision.length > 0) {
      (data as { scope_revision?: string }).scope_revision = scopeRevision;
    }
    if (clientGeneration !== usageDashboardClientGeneration) throw createAbortError();
    return data;
  })();
  usageDashboardOverviewInFlight.set(key, request);
  usageDashboardOverviewControllers.set(key, ownerController);
  // A caller's deadline aborts only that consumer; do not evict the shared
  // owner until its HTTP/JSON work settles or a second Dashboard instance
  // could accidentally duplicate the expensive authorized request.
  void request.then(
    () => { expireUsageDashboardOverviewOwner(key, request, false); },
    () => { expireUsageDashboardOverviewOwner(key, request, false); },
  );
  return awaitOverviewConsumer(key, request, options?.signal, options?.expireInFlightOnAbort === true);
}

function expireUsageDashboardOverviewOwner(
  key: string, owner: Promise<UsageDashboardOverviewReport>, abort = true,
): void {
  if (usageDashboardOverviewInFlight.get(key) !== owner) return;
  usageDashboardOverviewInFlight.delete(key);
  const controller = usageDashboardOverviewControllers.get(key);
  usageDashboardOverviewControllers.delete(key);
  usageDashboardOverviewConsumerCounts.delete(key);
  if (abort) controller?.abort();
}

/** Detach one protected overview consumer without cancelling a shared owner. */
function awaitOverviewConsumer(
  key: string,
  owner: Promise<UsageDashboardOverviewReport>,
  signal?: AbortSignal,
  expireOwnerWhenLastConsumerDetaches = false,
): Promise<UsageDashboardOverviewReport> {
  usageDashboardOverviewConsumerCounts.set(key, (usageDashboardOverviewConsumerCounts.get(key) ?? 0) + 1);
  let released = false;
  const release = (allowOwnerExpiry: boolean) => {
    if (released) return;
    released = true;
    const remaining = Math.max(0, (usageDashboardOverviewConsumerCounts.get(key) ?? 1) - 1);
    if (remaining === 0) {
      usageDashboardOverviewConsumerCounts.delete(key);
      // A timed-out reauthorization may release bookkeeping and permit a
      // later retry only when it was the final consumer. A fresh sibling keeps
      // the exact HTTP owner alive and coalesced.
      if (allowOwnerExpiry) expireUsageDashboardOverviewOwner(key, owner);
    } else {
      usageDashboardOverviewConsumerCounts.set(key, remaining);
    }
  };
  if (signal?.aborted) {
    release(expireOwnerWhenLastConsumerDetaches);
    return Promise.reject(createAbortError());
  }
  if (!signal) {
    return owner.finally(() => release(false));
  }
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      signal.removeEventListener('abort', onAbort);
      release(expireOwnerWhenLastConsumerDetaches);
      reject(createAbortError());
    };
    signal.addEventListener('abort', onAbort, { once: true });
    owner.then(
      (value) => { signal.removeEventListener('abort', onAbort); release(false); resolve(value); },
      (error) => { signal.removeEventListener('abort', onAbort); release(false); reject(error); },
    );
  });
}

/**
 * Fetch organization and current-unit adoption metrics for a date range.
 */
export async function fetchOrganizationalAdoption(
  start: string,
  end: string,
  inFlightScopeKey?: string
): Promise<OrganizationalAdoptionReport> {
  const key = usageDashboardInFlightKey(start, end, [], inFlightScopeKey);
  const existing = organizationalAdoptionInFlight.get(key);
  if (existing) return existing;
  const clientGeneration = usageDashboardClientGeneration;
  const request = (async (): Promise<OrganizationalAdoptionReport> => {
    const response = await fetchWithChatFeedback(
      `${API_BASE_URL}/api/admin/organizational-adoption?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
      { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => undefined);
      const normalized = normalizeApiError(error);
      const err = new Error(normalized.message) as Error & {
        status?: number;
        error_code?: string;
      };
      err.status = response.status;
      if (normalized.errorCode) err.error_code = normalized.errorCode;
      throw err;
    }
    const data = await response.json();
    if (clientGeneration !== usageDashboardClientGeneration) throw createAbortError();
    return data;
  })();
  organizationalAdoptionInFlight.set(key, request);
  try {
    return await request;
  } finally {
    if (organizationalAdoptionInFlight.get(key) === request) organizationalAdoptionInFlight.delete(key);
  }
}

/**
 * Fetch joined chat-feedback events (prompts + responses) for a date range
 */
export async function fetchChatFeedback(start: string, end: string): Promise<ChatFeedbackSummary> {
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/admin/chat-feedback?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
    { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch chat feedback' }));
    const err = new Error(extractDetailMessage(error.detail, `HTTP ${response.status}`)) as any;
    err.status = response.status;
    throw err;
  }
  return response.json();
}

/**
 * Fetch the count of unread chat-feedback entries (super-only).
 * `GET /api/admin/chat-feedback/unread-count` returns `{ count: <int> }`.
 * Fail-safe: any error → 0 (the badge is simply hidden, never a crash).
 */
export async function fetchUnreadFeedbackCount(): Promise<number> {
  try {
    const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/admin/chat-feedback/unread-count`, {
      method: 'GET',
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    if (!response.ok) return 0;
    const data = await response.json();
    return typeof data?.count === 'number' ? data.count : 0;
  } catch {
    return 0;
  }
}

/**
 * Mark the given chat-feedback prompt ids viewed (super-only).
 * `POST /api/admin/chat-feedback/mark-viewed` with `{ prompt_ids }` returns
 * the fresh `{ count: <int> }`. Fail-safe: any error → 0.
 */
export async function markChatFeedbackViewed(promptIds: string[]): Promise<number> {
  try {
    const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/admin/chat-feedback/mark-viewed`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ prompt_ids: promptIds }),
      credentials: 'include',
    });
    if (!response.ok) return 0;
    const data = await response.json();
    return typeof data?.count === 'number' ? data.count : 0;
  } catch {
    return 0;
  }
}

/**
 * Fetch detailed stage data for a specific flow
 */
export async function fetchFlowDetail(flowId: string, start: string, end: string): Promise<FlowDetail> {
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/admin/flow-detail?flow_id=${encodeURIComponent(flowId)}&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
    { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch flow detail' }));
    const err = new Error(extractDetailMessage(error.detail, `HTTP ${response.status}`)) as any;
    err.status = response.status;
    err.retryAfter = error.retry_after;
    throw err;
  }
  return response.json();
}

/**
 * Fetch all email-sent timestamps
 */
export async function fetchEmailSent(): Promise<Record<string, string>> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/admin/email-sent`, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch email-sent data' }));
    throw new Error(extractDetailMessage(error.detail, `HTTP ${response.status}`));
  }
  return response.json();
}

/**
 * Fetch all reminder-sent timestamps
 */
export async function fetchRemindedUsers(): Promise<Record<string, string>> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/admin/reminded-users`, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include',
  });
  if (!response.ok) {
    return {};
  }
  return response.json();
}

/**
 * Save an email-sent timestamp for a flow
 */
export async function saveEmailSent(flowId: string, emailSentAt: string, sentBy?: string): Promise<void> {
  const body: Record<string, string> = { flow_id: flowId, email_sent_at: emailSentAt };
  if (sentBy) body.sent_by = sentBy;
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/admin/email-sent`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to save email-sent' }));
    throw new Error(extractDetailMessage(error.detail, `HTTP ${response.status}`));
  }
}

/**
 * Delete an email-sent entry for a flow
 */
export async function deleteEmailSent(flowId: string): Promise<void> {
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/admin/email-sent?flow_id=${encodeURIComponent(flowId)}`,
    { method: 'DELETE', headers: getAuthHeaders(), credentials: 'include' }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete email-sent' }));
    throw new Error(extractDetailMessage(error.detail, `HTTP ${response.status}`));
  }
}

/**
 * Fetch the canonical unit list from the backend
 */
export async function fetchUnits(): Promise<string[]> {
  const res = await fetchWithChatFeedback(`${API_BASE_URL}/api/admin/units`, { credentials: 'include' });
  if (!res.ok) return [];
  const data = await res.json();
  return data.units ?? [];
}

/**
 * Add a new unit to the canonical unit list
 */
export async function addUnit(unit: string): Promise<string[]> {
  const res = await fetchWithChatFeedback(`${API_BASE_URL}/api/admin/units`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ unit }),
  });
  if (!res.ok) return [];
  const data = await res.json();
  return data.units ?? [];
}

/**
 * Add an email to the whitelist
 */
export async function addWhitelistEmail(
  email: string,
  role: string,
  unit?: string
): Promise<{ status: string; email: string }> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/admin/whitelist`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ email, role, ...(unit ? { unit } : {}) }),
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to add whitelist email' }));
    throw new Error(extractDetailMessage(error.detail, `HTTP ${response.status}`));
  }
  return response.json();
}

/**
 * Fetch pending access requests (super-only).
 * `GET /api/admin/pending-requests?status=pending|approved|rejected|all`
 * Returns `ApiResponse` with `data = { requests: AccessRequestRecord[] }` (newest-first).
 */
export async function fetchPendingRequests(
  status: string = 'pending'
): Promise<AccessRequestRecord[]> {
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/admin/pending-requests?status=${encodeURIComponent(status)}`,
    { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch pending requests' }));
    const err = new Error(extractDetailMessage(error.detail, `HTTP ${response.status}`)) as any;
    err.status = response.status;
    throw err;
  }
  const data = await response.json();
  return data?.data?.requests ?? [];
}

/**
 * Fetch the newest accepted users (any-admin).
 * `GET /api/admin/accepted-users` returns `ApiResponse` with
 * `data = { users: NewestUserRecord[] }` (newest-first). The backend
 * unit-scopes the rows for non-super admins.
 */
export async function fetchNewestUsers(): Promise<NewestUserRecord[]> {
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/admin/accepted-users`,
    { method: 'GET', headers: getAuthHeaders(), credentials: 'include' }
  );
  if (!response.ok) return [];
  const data = await response.json();
  return data?.data?.users ?? [];
}

/**
 * Approve a pending access request (super-only).
 * `POST /api/admin/pending-requests/{id}/approve` with `{ unit, role }`.
 */
export async function approvePendingRequest(
  id: string,
  unit: string,
  role: string = 'regular'
): Promise<void> {
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/admin/pending-requests/${encodeURIComponent(id)}/approve`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ unit, role }),
      credentials: 'include',
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to approve request' }));
    throw new Error(extractDetailMessage(error.detail, `HTTP ${response.status}`));
  }
}

/**
 * Reject a pending access request (super-only).
 * `POST /api/admin/pending-requests/{id}/reject` with `{ reason }`.
 */
export async function rejectPendingRequest(id: string, reason: string): Promise<void> {
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/admin/pending-requests/${encodeURIComponent(id)}/reject`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason }),
      credentials: 'include',
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to reject request' }));
    throw new Error(extractDetailMessage(error.detail, `HTTP ${response.status}`));
  }
}
